import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import { differenceInMinutes, addHours, startOfDay } from 'date-fns'
import * as _ from 'lodash'
import { useSelector } from 'react-redux'
import { AVAILABILITY_COLLECTION, BOOKING_COLLECTION, BOOKING_TYPE_ACTIVITY, BOOKING_TYPE_FLIGHT, BOOKING_TYPE_MAINTENANCE, BOOKING_TYPE_RENTING } from '../../_constants/globals'
import { useFirestore } from '../../hooks/useFirestore'
import useAuth from '../../hooks/useAuth'
import PseudoBorderLine from './PseudoBorderLine'
import Slot from './Slot'


const PlanningRow = ({ resource, day }) => {
  
  const profile = useAuth().getProfile()
  const adminMode = useSelector(state => state.data.adminMode)
  const availabilityHooks = useFirestore(AVAILABILITY_COLLECTION, { storeAs: 'dailyAvailabilities' })
  const availabilities = availabilityHooks.getDocs()
  const bookingHooks = useFirestore(BOOKING_COLLECTION, { storeAs: 'PlanningTable' })
  const dbBookings = bookingHooks.getDocs()
  
  const bookings = useMemo(
    () => resource?.lastCounter
      ? dbBookings?.filter(data => data.aircraftRef?.id === resource.id)
      : dbBookings?.filter(data => data.instructorRef?.id === resource.id),
    [resource, dbBookings],
  )
  
  const cells = useMemo(() => {
    // Create a 48 long array of free or unavailable slots
    let arr = Array.from(Array(48)).map((val, key) =>
      ({
        id: (resource.bookable ? 'free' : 'unavailable') + '-' + resource.id + '-' + key,
        start: key,
        slotType: resource.bookable ? 'free' : 'unavailable',
        resource: { ...resource, type: resource.email ? 'instructor' : 'aircraft' },
        day,
      }),
    )
    
    // Replace free slot with bookings
    if (bookings)
      arr = _.reduceRight(bookings, (acc, val) => {
        const startIndex = val.startDate < addHours(startOfDay(day), 8)
          ? 0
          : differenceInMinutes(val.startDate, addHours(startOfDay(val.startDate), 8)) / 15
        const bookLength = val.startDate > addHours(startOfDay(day), 8)
          ? differenceInMinutes(val.endDate, val.startDate) / 15
          : differenceInMinutes(val.endDate, addHours(startOfDay(day), 8)) / 15
        for (let i = startIndex; i < Math.min(startIndex + bookLength, 48); i++) {
          let label = 'Unknown type'
          if (val.bookingType === BOOKING_TYPE_FLIGHT)
            label = val.activity?.title || val.user?.firstname
          else if (val.bookingType === BOOKING_TYPE_MAINTENANCE)
            label = 'Maintenance'
          else if (val.bookingType === BOOKING_TYPE_RENTING)
            label = 'Location'
          else if (val.bookingType === BOOKING_TYPE_ACTIVITY)
            label = val.activity.title
          acc[i] = {
            id: val.id,
            start: startIndex,
            slotType: 'book',
            resource: { ...resource, type: resource.email ? 'instructor' : 'aircraft' },
            day,
            activityRef: val.activityRef,
            activity: val.activity,
            booking: val,
            type: val.flightType?.name,
            user: val.pilot || val.user,
            instructorId: val.instructorRef?.id,
            userId: (val.pilotRef || val.userRef)?.id,
            bookingType: val.bookingType,
            label,
          }
        }
        return acc
      }, arr)
    
    // Replace free slots with availabilities
    if (availabilities)
      arr = availabilities.reduceRight((acc, val) => {
        const startIndex = differenceInMinutes(val.startDate, addHours(startOfDay(val.startDate), 8)) / 15
        const bookLength = differenceInMinutes(val.endDate, val.startDate) / 15
        for (let i = Math.max(0, startIndex); i < startIndex + bookLength; i++) {
          if (acc[i] && acc[i].slotType === 'unavailable')
            acc[i].slotType = 'free'
        }
        return acc
      }, arr)
    
    // Groups timeslots
    const data = []
    let beginning
    for (let i = 0; i < arr.length; i++) {
      if (beginning !== undefined
        && arr[i].slotType === arr[beginning].slotType
        && arr[i].slotType !== 'free'
        && (arr[i].slotType === 'unavailable' || (arr[beginning].id && arr[beginning].id === arr[i].id))
      )
        data[data.length - 1].length++
      else {
        data.push({
          ...arr[i],
          length: 1,
        })
        beginning = i
      }
    }
    
    return data
  }, [resource, bookings, availabilities, day, profile, adminMode])
  
  return (
    <>
      <PseudoBorderLine />
      <Box component='tr' sx={{ borderLeft: '1px solid #ccc' }}>
        <Box component='th' colSpan={3} sx={{
          position: 'relative',
          padding: '0 0 0 3px',
          background: '#f2f2f2',
        }}>
          <Box sx={{
            width: '100%',
            padding: '.3em .3em .3em 3px',
            lineHeight: '1.2em',
            textAlign: 'left',
          }}>
            {resource.name}<br/>
            {resource.type}
          </Box>
        </Box>
        {cells.map(cell => <Slot key={cell.id} {...cell} />)}
      </Box>
      <PseudoBorderLine />
    </>
  )
}

PlanningRow.propTypes = {
  resource: PropTypes.object,
  bookings: PropTypes.array,
  day: PropTypes.object.isRequired,
}

export default PlanningRow
